import {
    ButtonGradientPrimary,
    Card,
    Colors,
    DropdownMenu,
    Heading,
    Icon,
    Pagination,
    Spinner,
    Text,
    type OptionProps,
} from '@amelio/ui-library';
import { useRef, useState } from 'react';

import type { TranslationKey } from '@/i18n-key';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { User } from '@/types/directory/User';

import { EvaluationCard } from '@/components/EvaluationCard/EvaluationCard';
import { RocketIllustration } from '@/components/Illustrations/Illustration';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useHandleClickOutside } from '@/hooks/utils/useHandleClickOutside';
import { getReviewTypeIcon, getReviewTypeLabel, hasRespondents } from '@/utils/cycleInstance';
import { createUserLookup } from '@/utils/evaluationCardHelpers';
import { getRevieweeIdsList, getReviewees } from '@/utils/reviewAudience';

const PAGE_SIZE = 10;

type RevieweeBadgeLabels = {
    primaryLabel: string;
    secondaryLabel: string;
};

const getRevieweeBadgeLabels = (
    t: (key: TranslationKey, options?: Record<string, unknown>) => string,
    profileName?: string,
): RevieweeBadgeLabels => {
    return {
        primaryLabel:
            profileName ??
            t('evaluation360Dashboard.reviewsToBeLaunched.revieweeBadge.noAssignedProfile'),
        secondaryLabel: t('evaluation360Dashboard.reviewsToBeLaunched.revieweeBadge.profile'),
    };
};

export interface ReviewsToBeLaunchedProps {
    reviewsToBeLaunched: CycleInstance[];
    isLoading: boolean;
    currentPage: number;
    totalCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    createCycleInstanceIsPending: boolean;
    users: User[];
    profileNameByUserId?: Record<string, string>;
    buttonOptions: OptionProps[];
    actionMenuItems: (cycleInstance: CycleInstance) => OptionProps[];
    onPrepareForLaunchClick: (cycleInstance: CycleInstance) => void;
    hasActiveFilters?: boolean;
}

export const ReviewsToBeLaunched = ({
    reviewsToBeLaunched,
    isLoading,
    currentPage,
    totalCount,
    totalPages,
    onPageChange,
    createCycleInstanceIsPending,
    users,
    profileNameByUserId = {},
    buttonOptions,
    actionMenuItems,
    onPrepareForLaunchClick,
    hasActiveFilters,
}: ReviewsToBeLaunchedProps) => {
    const { t } = useAmelioTranslations();
    const { getAllReviewers } = createUserLookup(users);

    const [buttonClicked, setButtonClicked] = useState(false);
    const dropdownContainerRef = useRef<HTMLDivElement>(null);

    useHandleClickOutside({
        clicked: buttonClicked,
        dropdownContainerRef,
        setButtonClicked,
    });

    // Only when there is nothing to show: swapping a populated list for bones collapses the
    // card height mid-fetch, and the browser clamps the page scroll.
    const showSkeleton = isLoading && reviewsToBeLaunched.length === 0;

    return (
        <Card
            className={
                'gradient-border-secondary mt-5 !px-4 !py-4 sm:!px-6 sm:!py-6 md:!px-8 md:!py-6 lg:!px-15 lg:!pt-8 lg:!pb-12'
            }
            padding={'none'}
            borderRadius={'xl'}
            variant={'flat'}
        >
            <RocketIllustration className={'relative -top-24 left-[66%] h-32 w-32 sm:left-4/5'} />

            <div className={'-mt-24 mb-6 md:w-1/2'}>
                <Heading
                    level={3}
                    className={'text-heading-sm! leading-relaxed'}
                >
                    {t('evaluation360Dashboard.reviewsToBeLaunched.title')}
                </Heading>

                <Text className={'mt-2'}>
                    {t('evaluation360Dashboard.reviewsToBeLaunched.description')}
                </Text>
            </div>

            {showSkeleton ? (
                <Skeleton className={'my-6'}>
                    <Bone className={'h-24 rounded-2xl'} />

                    <Bone className={'my-4 h-24 rounded-2xl'} />

                    <Bone className={'h-24 rounded-2xl'} />
                </Skeleton>
            ) : (
                <>
                    {reviewsToBeLaunched.length === 0 && hasActiveFilters && (
                        <Text className={'py-6 text-center'}>
                            {t('evaluation360Dashboard.noFilterResults')}
                        </Text>
                    )}

                    {reviewsToBeLaunched &&
                        reviewsToBeLaunched.length > 0 &&
                        reviewsToBeLaunched.map((review) => {
                            const reviewees = getReviewees({
                                reviewAudience: review.reviewAudience,
                                users,
                            });

                            const revieweeIds = review.reviewAudience
                                ? getRevieweeIdsList(review.reviewAudience)
                                : [];

                            const revieweeBadgeLabels = getRevieweeBadgeLabels(
                                t,
                                revieweeIds.length === 1
                                    ? profileNameByUserId[revieweeIds[0]]
                                    : undefined,
                            );

                            const onClick = () => onPrepareForLaunchClick(review);

                            return (
                                <EvaluationCard
                                    className={'my-2'}
                                    key={review.id}
                                    date={review.reviewSchedule.scheduledAt}
                                    infoBadges={[
                                        {
                                            icon: getReviewTypeIcon(review.reviewType),
                                            id: `${review.id}-evaluation-type`,
                                            primaryLabel: getReviewTypeLabel(review.reviewType),
                                            secondaryLabel: t('common.evaluationType'),
                                        },
                                        {
                                            icon: 'business-chart',
                                            id: `${review.id}-badge-2`,
                                            primaryLabel: revieweeBadgeLabels.primaryLabel,
                                            secondaryLabel: revieweeBadgeLabels.secondaryLabel,
                                        },
                                    ]}
                                    primaryAction={{
                                        label: t(
                                            'evaluation360Dashboard.reviewsToBeLaunched.prepareForLaunch',
                                        ),
                                        onClick,
                                        variant: 'outlined',
                                    }}
                                    reviewers={
                                        hasRespondents(review.reviewType)
                                            ? getAllReviewers(review?.reviewEvaluatorSelection)
                                            : undefined
                                    }
                                    user={reviewees.length === 1 ? reviewees[0] : undefined}
                                    users={reviewees}
                                    variant={'ToBeLaunched360'}
                                    actionMenuItems={actionMenuItems(review)}
                                    onCardClick={onClick}
                                />
                            );
                        })}
                </>
            )}

            <Pagination
                variant={'rounded'}
                showResultsSummary={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={onPageChange}
                isLoading={isLoading}
                dataTestId={'reviews-to-be-launched-pagination'}
                resultsSummaryText={t('common.showingResults', {
                    start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                    end: Math.min(currentPage * PAGE_SIZE, totalCount),
                    total: totalCount,
                })}
            />

            <div
                className={'relative mt-4 w-fit'}
                ref={dropdownContainerRef}
                tabIndex={-1}
                onBlur={(e) => {
                    if (!dropdownContainerRef.current?.contains(e.relatedTarget as Node)) {
                        setButtonClicked(false);
                    }
                }}
            >
                <ButtonGradientPrimary
                    className={'flex items-center'}
                    leftIcon={
                        createCycleInstanceIsPending ? null : (
                            <Icon
                                name={'essential-add-circle'}
                                color={Colors.WHITE}
                                className={'mr-2'}
                                size={'xl'}
                            />
                        )
                    }
                    onClick={() => setButtonClicked(!buttonClicked)}
                    disabled={createCycleInstanceIsPending}
                >
                    {createCycleInstanceIsPending ? (
                        <Spinner
                            size={'xs'}
                            color={Colors.PRIMARY_700}
                        />
                    ) : (
                        <Text color={Colors.WHITE}>
                            {t('evaluation360Dashboard.reviewsToBeLaunched.createNewEvaluation')}
                        </Text>
                    )}
                </ButtonGradientPrimary>

                <DropdownMenu
                    className={'relative top-2 z-50'}
                    open={buttonClicked}
                    placement={'top-right'}
                    options={buttonOptions}
                    onOptionClick={(option) => {
                        option.onClick?.();
                    }}
                />
            </div>
        </Card>
    );
};
